import React, { useEffect, useCallback } from 'react';
import {
	BDSProvider,
	Box,
	Button,
	Drawer,
	Typography,
} from '@barracuda-internal/bds-core-v2';
import Add from '@barracuda-internal/bds-core-v2/dist/Icons/Core/Add';
import SortIndeterminate from '@barracuda-internal/bds-core-v2/dist/Icons/Core/SortIndeterminate';
import GridPanel from './GridPanel';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import useAuth from 'src/hooks/useAuth';
import { PolicySettings } from './PolicySettings';
import PolicySearchField from './PolicySearchField';
import { Link } from 'react-router-dom';
import ToastComponent from 'src/components/toast/ToastComponent';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from 'src/redux/store';
import {
	setSearch,
	setSearchText,
	setPolicyChange,
	resetPolicyFeedsToPrev,
	openCreatePolicyDrawer,
	clearToast,
	closeDrawer,
} from 'src/features/policyengine/policyEngineSlice';
import {
	fetchPolicyFeeds,
	fetchConsent,
	updatePriorities,
} from 'src/features/policyengine/thunks';

const PolicyEnginePage = (): React.ReactElement => {
	const { api } = useAppToolchain();
	const { auth } = useAuth();
	const dispatch = useDispatch<AppDispatch>();

	const {
		policyFeeds,
		prevFeedsResponse,
		loading,
		hideConsent,
		search,
		searchText,
		policyChange,
		drawerOpen,
		toast,
	} = useSelector((state: RootState) => state.policyEngine);

	const accountId = auth.accountDetails.account.id;

	useEffect(() => {
		dispatch(fetchPolicyFeeds({ api, accountId, search }));
	}, [dispatch, api, accountId, search]);

	useEffect(() => {
		dispatch(fetchConsent({ api, accountId }));
	}, [dispatch, api, accountId]);

	useEffect(() => {
		if (toast) {
			const timer = setTimeout(() => {
				dispatch(clearToast());
			}, toast.duration || 3000);
			return () => clearTimeout(timer);
		}
	}, [toast, dispatch]);

	const handleResetPriority = () => {
		dispatch(resetPolicyFeedsToPrev());
		dispatch(setPolicyChange(false));
	};

	const handleSavePriority = () => {
		const priorityChange = policyFeeds.reduce((acc, curr, index) => {
			if (curr.id !== prevFeedsResponse[index]?.id) {
				acc.push({
					id: curr.id,
					priority: index + 1,
				});
			}
			return acc;
		}, [] as { id: string; priority: number }[]);

		if (priorityChange.length === 0) {
			dispatch(setPolicyChange(false));
			return;
		}

		dispatch(updatePriorities({ api, accountId, priorities: priorityChange }));
	};

	const handleCreatePolicy = () => {
		dispatch(openCreatePolicyDrawer());
	};

	const handleCloseDrawer = () => {
		dispatch(closeDrawer());
	};

	const handleEditPriority = () => {
		dispatch(setSearch(''));
		dispatch(setSearchText(''));
		dispatch(setPolicyChange(true));
	};

	const handleSearchChange = useCallback((value: string) => {
		dispatch(setSearch(value));
	}, [dispatch]);

	const handleSearchTextChange = useCallback((value: string) => {
		dispatch(setSearchText(value));
	}, [dispatch]);

	const toastId = React.useMemo(() => (toast ? Date.now() : null), [toast]);
	const toasts = toast
		? [{ id: toastId, type: toast.type, message: toast.message, duration: toast.duration }]
		: [];

	return (
		<BDSProvider>
			{!hideConsent && (
				<Box
					sx={{
						textAlign: 'center',
						backgroundColor: '#FBEAEA',
						color: '#581313',
						padding: '8px',
					}}
				>
					Policies for this account will be limited to users. Please{' '}
					<Link to="/settings/connectors">reauthorise the tenant</Link> to allow Data
					Inspector to read groups data.
				</Box>
			)}
			<Box sx={{ marginLeft: '24px', marginRight: '24px' }}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						height: '75px',
						marginBottom: '8px',
					}}
				>
					{policyChange && (
						<Typography variant="h1" color="textPrimary">
							Edit Policy Priorities
						</Typography>
					)}
					<Box
						sx={{
							marginLeft: 'auto',
							display: 'flex',
							alignItems: 'center',
						}}
					>
						{!policyChange && (
							<Button
								bdsType="interactiveSubtle"
								onClick={handleEditPriority}
							>
								<SortIndeterminate fontSize="small" sx={{ marginRight: '8px' }} />
								Edit Priority
							</Button>
						)}
						{!policyChange && (
							<Box
								sx={{
									backgroundColor: '#bdbdbd',
									marginLeft: '35px',
									marginRight: '35px',
									width: '2px',
									height: '40px',
								}}
							/>
						)}
						{!policyChange && (
							<Button onClick={handleCreatePolicy}>
								<Add fontSize="small" sx={{ marginRight: '8px' }} />
								Create Policy
							</Button>
						)}

						{policyChange && (
							<Button
								bdsType="interactiveSubtle"
								sx={{ margin: '16px' }}
								onClick={handleResetPriority}
							>
								Cancel
							</Button>
						)}
						{policyChange && (
							<Button onClick={handleSavePriority}>
								Save
							</Button>
						)}
					</Box>
				</Box>
				<PolicySearchField
					setSearch={handleSearchChange}
					searchText={searchText}
					setSearchText={handleSearchTextChange}
					label="Search"
				/>

				<GridPanel
					policyFeeds={policyFeeds || []}
					isLoading={loading}
				/>

				<Drawer anchor="right" open={drawerOpen} onClose={handleCloseDrawer}>
					<PolicySettings anchor={'right'} />
				</Drawer>
			</Box>

			<ToastComponent toasts={toasts} position={'bottom-left'} />
		</BDSProvider>
	);
};

export default PolicyEnginePage;
